import { Document } from '@contentful/rich-text-types'
import { FC, useMemo } from 'react'

import { Accordion } from '@syconium/little-miss-figgy'

import { ContentfulRichText } from '../../../../../../app/_components/pages/contentful/components/ContentfulRichText'
import { useSiteWideMessaging } from '../../../../../../app/_providers/SiteWideMessagingProvider.client'
import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { trackEvent } from '../../../../../../lib/analytics'
import { ContentfulRichDocumentAsFigsComponents } from '../../../../../../lib/utils/contentful'
import { FitModelStateContainer } from '../../contexts/FitModelStateContainer'

import {
  AccordionBody,
  AccordionContainer,
  AccordionHeader,
  AccordionTitle,
  CareInstructions,
  FabricAndCare,
  FeaturesWrapper,
  FitModelsDescriptionsList,
  ListItem,
  ProductDetailsAndFitBody,
  RawMaterials,
  SectionWrapper,
} from './ProductDetailsAccordionSection.styles'

export interface ProductDetailsAccordionSectionProps {
  careInstructions: string | undefined | null
  fitModelsDescriptions: string[] | undefined | null
  features: Document | undefined | null
  materials: string[] | undefined | null
}

export const ProductDetailsAccordionSection: FC<ProductDetailsAccordionSectionProps> = ({
  careInstructions,
  fitModelsDescriptions,
  features,
  materials,
}) => {
  const {
    detailsSection: {
      accordionTitles: { fabricAndCareTitle, productDetailsTitle },
    },
  } = useTranslation()
  const { shippingAndReturnsTitle, shippingAndReturnsText } = useSiteWideMessaging()

  const { fitModel } = FitModelStateContainer.useContainer()
  const computedFitModelDescriptions = useMemo(() => {
    // If there is an active fit model, do not print out model descriptions
    if (fitModel?.description) return []

    return fitModelsDescriptions ? fitModelsDescriptions : []
  }, [fitModel?.description, fitModelsDescriptions])
  return (
    <SectionWrapper>
      <AccordionContainer
        {...trackEvent({ category: 'product details', action: 'click accordion' })}
      >
        <Accordion variant='filled' icon='plus' aria-label='Detail Section' showBorder>
          <Accordion.Section id={0}>
            <AccordionHeader>
              <AccordionTitle textTransform='uppercase' asTag='p'>
                {productDetailsTitle}
              </AccordionTitle>
            </AccordionHeader>
            <AccordionBody>
              <ProductDetailsAndFitBody>
                {computedFitModelDescriptions.length > 0 && (
                  <FitModelsDescriptionsList>
                    {computedFitModelDescriptions.map((fitModelsDescription, index) => (
                      <ListItem key={index}>{fitModelsDescription}</ListItem>
                    ))}
                  </FitModelsDescriptionsList>
                )}
                <div data-testid='product-details'>
                  <FeaturesWrapper>
                    {features ? (
                      <ContentfulRichDocumentAsFigsComponents
                        contentfulDocument={features}
                        options={{ bodyFontSize: 'BodySmall', bodyFontWeight: '400' }}
                      />
                    ) : null}
                  </FeaturesWrapper>
                </div>
              </ProductDetailsAndFitBody>
            </AccordionBody>
          </Accordion.Section>
          {materials?.length || careInstructions ? (
            <Accordion.Section id={2}>
              <AccordionHeader>
                <AccordionTitle textTransform='uppercase' asTag='p'>
                  {fabricAndCareTitle}
                </AccordionTitle>
              </AccordionHeader>
              <AccordionBody>
                <FabricAndCare data-testid='fabric-and-care'>
                  {materials?.length ? (
                    <RawMaterials>
                      {materials.map((material, index) => (
                        <ListItem key={index}>{material}</ListItem>
                      ))}
                    </RawMaterials>
                  ) : null}

                  {careInstructions && <CareInstructions>{careInstructions}</CareInstructions>}
                </FabricAndCare>
              </AccordionBody>
            </Accordion.Section>
          ) : null}
          {shippingAndReturnsTitle && shippingAndReturnsText ? (
            <Accordion.Section id={3}>
              <AccordionHeader>
                <AccordionTitle textTransform='uppercase' asTag='p'>
                  {shippingAndReturnsTitle}
                </AccordionTitle>
              </AccordionHeader>
              <AccordionBody>
                <ContentfulRichText content={shippingAndReturnsText} />
              </AccordionBody>
            </Accordion.Section>
          ) : null}
        </Accordion>
      </AccordionContainer>
    </SectionWrapper>
  )
}
